import { ComponentProps, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { RocketLaunchOutlined, SvgIconComponent } from '@mui/icons-material';
import SettingsIcon from '@mui/icons-material/Settings';
import CalendarIcon from '@mui/icons-material/CalendarTodayOutlined';
import useGetOnboarding from '../../../../hooks/businesses/useGetOnboarding';
import useUser from '../../../../hooks/global/useUser';
import { LoadedUser } from '../../../../interfaces';
import { OnboardingModalCategory } from '../../../../types/businesses';
import Progress from '../../../global/ProgressBar';
import useMeetWithSpecialist from '../../../../hooks/businesses/useMeetWithSpecialist';
import IFrameModal from '../../../modal/IFrameModal';
import useLoadingModal from '../../../../hooks/global/useLoadingModal';

interface Props {
  sx?: SxProps<Theme>;
  onMinimize?: () => void;
  onOpenModal: (category: OnboardingModalCategory) => void;
}

interface ButtonProps {
  variant?: ComponentProps<typeof Button>['variant'];
  color?: ComponentProps<typeof Button>['color'];
  onClick: () => void | Promise<void>;
  text: string;
  icon: SvgIconComponent;
  isFullWidth?: boolean;
  isLoading?: boolean;
  progress?: {
    completed: number;
    total: number;
  };
}

const BUTTONS_GAP = 1;

function OnboardStepButton({
  variant,
  color,
  onClick,
  text,
  progress,
  icon,
  isFullWidth,
  isLoading,
}: ButtonProps) {
  const theme = useTheme();
  const Icon = icon;
  return (
    <LoadingButton
      loading={isLoading}
      disabled={isLoading}
      disableRipple
      variant={variant || 'light'}
      color={color || 'info'}
      onClick={onClick}
      sx={{
        py: 3.5,
        justifyContent: 'start',
        width: {
          xs: '100%',
          md: isFullWidth
            ? '100%'
            : `calc(50% - ${theme.spacing(BUTTONS_GAP / 2)})`,
        },
      }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="start"
        gap={2.5}
        width="100%">
        <Icon
          sx={{
            width: '20px',
            height: '20px',
          }}
          htmlColor={theme.palette.darkGrey.main}
        />
        <Typography
          variant="h3"
          component="p"
          fontSize="1rem"
          color={theme.palette.darkGrey.main}>
          {text}
        </Typography>
        {progress && progress?.total > 0 && (
          <Typography
            variant="body2"
            color={theme.palette.medGrey.main}
            sx={{
              ml: 'auto',
            }}>
            {progress.completed} / {progress.total} completed
          </Typography>
        )}
      </Box>
    </LoadingButton>
  );
}

function FullOnboardingProgress({ sx, onMinimize, onOpenModal }: Props) {
  const { user } = useUser<LoadedUser>();
  const { percentCompleted, categoryCompleted, getCategoryProgress } =
    useGetOnboarding();
  const { mutateAsync, isPending: isPendingMeet } = useMeetWithSpecialist();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();

  const [scheduleUrl, setScheduleUrl] = useState<string | null>(null);

  const isActivateComplete = categoryCompleted.ACTIVATE;
  const isConfigureComplete = categoryCompleted.CONFIGURE;

  const onMeetWithSpecialist = async () => {
    openLoadingModal({
      header: 'Loading Calendar...',
      message: 'Please wait while the calendar is loading',
    });
    try {
      const result = await mutateAsync({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        company: user.business.name || '',
      });
      if (!result?.url) return;
      setScheduleUrl(result.url);
    } finally {
      closeLoadingModal();
    }
  };

  return (
    <>
      <Stack gap={2.5} py={2.5} sx={sx}>
        <Box
          display="flex"
          alignItems="start"
          justifyContent="space-between"
          gap={1}>
          <Typography variant="h1" component="p" fontWeight="bold">
            Hi{user.firstName ? `, ${user.firstName}` : ''}!<br />
            Let&apos;s get you set up.
          </Typography>
          {onMinimize && (
            <Box flexShrink={0}>
              <Button variant="light" onClick={onMinimize}>
                <span>
                  I&apos;ll finish{' '}
                  <Typography
                    variant="inherit"
                    component="span"
                    display={{
                      xs: 'none',
                      sm: 'inline',
                    }}>
                    this{' '}
                  </Typography>
                  later
                </span>
              </Button>
            </Box>
          )}
        </Box>

        <Progress progress={percentCompleted} includePercentage />

        <Box
          flexWrap="wrap"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={BUTTONS_GAP}>
          {!isActivateComplete && (
            <OnboardStepButton
              icon={RocketLaunchOutlined}
              onClick={() => onOpenModal('ACTIVATE')}
              text="Activate your account"
              progress={getCategoryProgress('ACTIVATE')}
            />
          )}
          {!isConfigureComplete && (
            <OnboardStepButton
              icon={SettingsIcon}
              onClick={() => onOpenModal('CONFIGURE')}
              text="Configure shop settings"
              progress={getCategoryProgress('CONFIGURE')}
            />
          )}
          <OnboardStepButton
            color="primary"
            isFullWidth={!isActivateComplete && !isConfigureComplete}
            icon={CalendarIcon}
            onClick={onMeetWithSpecialist}
            text="Meet with an onboarding specialist"
            isLoading={isPendingMeet}
          />
        </Box>
      </Stack>
      <IFrameModal
        open={!!scheduleUrl}
        src={scheduleUrl}
        onClose={() => setScheduleUrl(null)}
        title="Schedule a meeting"
      />
    </>
  );
}

export default FullOnboardingProgress;
